// Ported from packages/desk/src/mobile/layout.ts (docs/PLAN-2026-09-21-mobile-os-shell.md, step 4). Same file name and props
// as the desk copy, the dk- prefix renamed rdr-, so a fix in one is a copy in the other. Radar never imports @gamo/desk.
/**
 * The phone home screen's rules, as pure functions so they are tested without a browser: how many columns the grid
 * has, which apps stand on the home screen and which live in the App Library, how a badge reads, the greeting by the
 * hour, the App Library's groups, what Search matches, and where a quick-actions menu sits.
 */
import type { RadarAppDefinition } from "../../apps/registry";
import type { BadgeValue } from "./types";

/** The narrowest phone that still gets three columns. Under it, two, so a label never shrinks below reading size. */
export const THREE_COLUMNS_FROM = 360;
/** At most this many apps on the home grid; the next slot is always More. */
export const MAX_HOME_APPS = 5;

export function gridColumns(width: number): 2 | 3 {
  return width < THREE_COLUMNS_FROM ? 2 : 3;
}

/**
 * The home grid: the apps with a `primary` position, in that order, at most five, then More. When no app says where it
 * stands, the first five visible apps. More is always there: it is also how the whole list is reached and searched.
 * Hidden apps are never shown.
 */
export function homeApps(apps: RadarAppDefinition[]): RadarAppDefinition[] {
  const visible = apps.filter((a) => !a.hidden);
  const placed = visible.filter((a) => typeof a.primary === "number").sort((a, b) => a.primary! - b.primary!);
  return (placed.length ? placed : visible).slice(0, MAX_HOME_APPS);
}

/** How a badge reads: nothing, a dot, or a count that stops at 99+. */
export function formatBadge(v: BadgeValue): { kind: "none" } | { kind: "dot" } | { kind: "count"; text: string; n: number } {
  if (v === true) return { kind: "dot" };
  if (typeof v !== "number" || !Number.isFinite(v) || v <= 0) return { kind: "none" };
  const n = Math.floor(v);
  return { kind: "count", text: n > 99 ? "99+" : String(n), n };
}

/** The badge for a screen reader, after the app's name: "3 new", or "something new". */
export function badgeLabel(v: BadgeValue): string | null {
  const b = formatBadge(v);
  return b.kind === "count" ? `${b.n} new` : b.kind === "dot" ? "something new" : null;
}

/** "Good morning" before noon, "Good afternoon" until five, "Good evening" after (and in the small hours). */
export function greetingFor(hour: number): string {
  if (hour >= 5 && hour < 12) return "Good morning";
  if (hour >= 12 && hour < 17) return "Good afternoon";
  return "Good evening";
}

/** Initials for the system bar and the avatar: the first letters of the first two words. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? [words[0]![0], words[1]![0]] : [...(words[0] ?? "").slice(0, 2)];
  return letters.join("").toUpperCase();
}

export const OTHER_GROUP = "Other";

/** The App Library's groups: the host's order first, then any other category by first appearance, then "Other". */
export function libraryGroups(apps: RadarAppDefinition[], categories: string[] = []): Array<{ label: string; apps: RadarAppDefinition[] }> {
  const visible = apps.filter((a) => !a.hidden);
  const order = [...categories];
  for (const a of visible) if (a.category && !order.includes(a.category)) order.push(a.category);
  const groups = order.map((label) => ({ label, apps: visible.filter((a) => a.category === label) })).filter((g) => g.apps.length > 0);
  const rest = visible.filter((a) => !a.category);
  if (rest.length) groups.push({ label: OTHER_GROUP, apps: rest });
  return groups;
}

/** Case and accents do not matter: "eleve" finds "Élève". */
export function fold(s: string): string {
  return s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().trim();
}

/** Whether every word typed appears in the text. An empty query matches nothing. */
export function matches(text: string, query: string): boolean {
  const q = fold(query).split(/\s+/).filter(Boolean);
  if (q.length === 0) return false;
  const t = fold(text);
  return q.every((w) => t.includes(w));
}

/** Apps whose name (or short name) matches, names that start with the query first. */
export function searchApps(apps: RadarAppDefinition[], query: string): RadarAppDefinition[] {
  const q = fold(query);
  return apps
    .filter((a) => !a.hidden && (matches(a.name, query) || (a.short ? matches(a.short, query) : false)))
    .sort((a, b) => Number(!fold(a.name).startsWith(q)) - Number(!fold(b.name).startsWith(q)));
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Where a quick-actions menu sits: above the icon when it fits, else below, never off the screen, with a gutter at each
 * side. Returns the menu's top left corner and which side of the icon it is on.
 */
export function placeMenu(anchor: Rect, menu: { w: number; h: number }, viewport: { w: number; h: number; top?: number; bottom?: number }, gap = 10, gutter = 12): { x: number; y: number; side: "above" | "below" } {
  const top = (viewport.top ?? 0) + gutter;
  const bottom = viewport.h - (viewport.bottom ?? 0) - gutter;
  const x = Math.min(Math.max(gutter, anchor.x + anchor.w / 2 - menu.w / 2), Math.max(gutter, viewport.w - gutter - menu.w));
  const above = anchor.y - gap - menu.h;
  if (above >= top) return { x, y: above, side: "above" };
  const below = anchor.y + anchor.h + gap;
  if (below + menu.h <= bottom) return { x, y: below, side: "below" };
  return { x, y: Math.max(top, Math.min(above, bottom - menu.h)), side: "above" };
}
